import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

interface ToolCardProps {
  title: string
  description: string
  path: string
  icon: React.ReactNode
}

const ToolCard: React.FC<ToolCardProps> = ({ title, description, path, icon }) => {
  return (
    <Link
      to={path}
      className='bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 flex flex-col items-center text-center'
    >
      <div className='text-primary mb-4 w-16 h-16 flex items-center justify-center rounded-full bg-primary bg-opacity-10'>
        {icon}
      </div>
      <h3 className='text-lg font-medium text-neutral-800 mb-2'>{title}</h3>
      <p className='text-neutral-600 text-sm'>{description}</p>
    </Link>
  )
}

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  // Tool definitions with icons
  const tools = [
    {
      title: 'Move Player & Helmet',
      description: 'Move a player and their assigned helmets to a different organization or team',
      path: '/move-player-helmet',
      icon: (
        <svg xmlns='http://www.w3.org/2000/svg' className='h-8 w-8' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
        </svg>
      )
    },
    {      title: 'Move Helmet Organization',
      description: 'Browse helmets by organization, then transfer to another organization',
      path: '/move-helmet-org',
      icon: (
        <svg xmlns='http://www.w3.org/2000/svg' className='h-8 w-8' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' />
        </svg>
      )
    },
    {
      title: 'Helmet Lookup',
      description: 'Look up helmet information by ID and optionally transfer between organizations',
      path: '/helmet-lookup',
      icon: (
        <svg xmlns='http://www.w3.org/2000/svg' className='h-8 w-8' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
        </svg>
      )
    },
    {
      title: 'Player Unit Swap',
      description: 'Replace a player unit with an existing player unit on the same team',
      path: '/player-unit-swap',
      icon: (
        <svg xmlns='http://www.w3.org/2000/svg' className='h-8 w-8' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 3v18' />
        </svg>
      )
    },
    {
      title: 'Support Dashboards',
      description: 'View embedded support reports and switch dashboards by organization need',
      path: '/support-dashboards',
      icon: (
        <svg xmlns='http://www.w3.org/2000/svg' className='h-8 w-8' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' />
        </svg>
      )
    }
  ]

  return (
    <div className='max-w-6xl mx-auto px-4 py-8'>
      <div className='mb-8'>
        <h1 className='text-2xl font-semibold text-neutral-800 mb-2'>Welcome, {user?.username || 'User'}</h1>
        <p className='text-neutral-600'>
          Select a tool from the dashboard below.
        </p>
      </div>

      {/* Stats Overview  TODO: implement later if desired */}
      {/*
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-8'>
        <div className='bg-white rounded-lg shadow p-6'>
          <h3 className='text-sm font-medium text-neutral-500 mb-1'>System Status</h3>
          <div className='flex items-center'>
            <div className='h-3 w-3 rounded-full bg-secondary mr-2' />
            <span className='text-lg font-semibold'>Online</span>
          </div>
        </div>

        <div className='bg-white rounded-lg shadow p-6'>
          <h3 className='text-sm font-medium text-neutral-500 mb-1'>Active Helmets</h3>
          <div className='text-lg font-semibold'>124</div>
        </div>

        <div className='bg-white rounded-lg shadow p-6'>
          <h3 className='text-sm font-medium text-neutral-500 mb-1'>Base Stations</h3>
          <div className='text-lg font-semibold'>16</div>
        </div>
      </div>
      */}

      {/* Tools Grid */}
      <h2 className='text-xl font-medium text-neutral-800 mb-4'>Available Tools</h2>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {tools.map((tool) => (
          <ToolCard
            key={tool.path}
            title={tool.title}
            description={tool.description}
            path={tool.path}
            icon={tool.icon}
          />
        ))}
      </div>

      {/* Recent Activity - TODO: Implement this feature */}
      {/* 
      <div className='mt-10'>
        <h2 className='text-xl font-medium text-neutral-800 mb-4'>Recent Activity</h2>
        <div className='bg-white rounded-lg shadow overflow-hidden'>
          <ul className='divide-y divide-neutral-200'>
            <li className='p-4'>
              <div className='flex items-center'>
                <div className='flex-1'>
                  <p className='text-sm font-medium text-neutral-800'>Helmet ID-4872 moved</p>
                  <p className='text-xs text-neutral-500'>2 hours ago</p>
                </div>
                <div>
                  <span className='px-2 py-1 text-xs rounded-full bg-primary/10 text-primary'>Move Helmet</span>
                </div>
              </div>
            </li>
            <li className='p-4'>
              <div className='flex items-center'>
                <div className='flex-1'>
                  <p className='text-sm font-medium text-neutral-800'>Base Station BS-12 restarted</p>
                  <p className='text-xs text-neutral-500'>4 hours ago</p>
                </div>
                <div>
                  <span className='px-2 py-1 text-xs rounded-full bg-error/10 text-error'>Restart</span>
                </div>
              </div>
            </li>
            <li className='p-4'>
              <div className='flex items-center'>
                <div className='flex-1'>
                  <p className='text-sm font-medium text-neutral-800'>Helmet ID-5631 and Player P-118 moved</p>
                  <p className='text-xs text-neutral-500'>Yesterday</p>
                </div>
                <div>
                  <span className='px-2 py-1 text-xs rounded-full bg-primary/10 text-primary'>Move Both</span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      */}
    </div>
  )
}

export default Dashboard
